import React from "react";
import { Link, useLocation } from "react-router-dom";
import { FileText, Plus, BarChart3, Hammer, Lock, Unlock, Settings, Wand2 } from "lucide-react";
import { useCenturion } from "../context/CenturionContext";
import { useCompany } from "../context/CompanyContext";

const Layout = ({ children, transparent = false }) => {
  const { pathname } = useLocation();
  const { unlocked, lock } = useCenturion();
  const { company } = useCompany();

  return (
    <div className="min-h-screen flex flex-col">
      <header className="no-print sticky top-0 z-30 backdrop-blur-md bg-[#07060a]/70 border-b border-[rgba(212,165,75,0.1)]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group">
            <div className="relative w-10 h-10 rounded-lg overflow-hidden" style={{ background: "linear-gradient(135deg, #e0bc62 0%, #b78827 100%)" }}>
              <FileText size={20} strokeWidth={2.2} className="text-[#1a1306] absolute inset-0 m-auto" />
            </div>
            <div className="leading-tight">
              <div className="text-[15px] font-semibold tracking-tight">WXZA <span className="text-[#d4a54b]">Forge™</span></div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#d4a54b]/70">Invoice Studio</div>
            </div>
          </Link>

          <nav className="flex items-center gap-1">
            <NavBtn to="/" active={pathname === "/"} icon={<BarChart3 size={14} />} label="Dashboard" />
            <NavBtn to="/studio/invoice" active={pathname.startsWith("/studio/invoice") || pathname === "/forge"} icon={<Hammer size={14} />} label="Invoice Studio" />
            <NavBtn to="/documents" active={pathname.startsWith("/documents") || pathname.startsWith("/studio/documents")} icon={<Wand2 size={14} />} label="Documents" />
            {unlocked && (
              <Link to="/invoices/new" className="btn-primary ml-2">
                <Plus size={14} /> NEW INVOICE
              </Link>
            )}
            <button
              onClick={() => unlocked ? lock() : null}
              title={unlocked ? "Issuer mode — click to lock" : "Locked — type 'centurion' to unlock"}
              className={`ml-2 w-9 h-9 flex items-center justify-center rounded-md border transition ${unlocked ? "border-[rgba(34,197,94,0.4)] text-[#22c55e] hover:bg-[rgba(34,197,94,0.1)]" : "border-[rgba(212,165,75,0.2)] text-[#d4a54b]/60 cursor-help"}`}
            >
              {unlocked ? <Unlock size={14} /> : <Lock size={14} />}
            </button>
            <Link
              to="/settings"
              title="Company settings"
              className={`ml-1 w-9 h-9 flex items-center justify-center rounded-md border transition ${pathname === "/settings" ? "border-[rgba(212,165,75,0.4)] text-[#e0bc62] bg-[rgba(212,165,75,0.1)]" : "border-[rgba(212,165,75,0.2)] text-[#d4a54b]/60 hover:text-[#e0bc62]"}`}
            >
              <Settings size={14} />
            </Link>
          </nav>
        </div>
      </header>

      <main className="flex-1">{children}</main>

      <footer className="no-print mt-auto px-6 py-8 border-t border-[rgba(212,165,75,0.1)]">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-3 text-[11px] text-white/40">
          <div>© 2026 {company.company_name} — {company.owner_name} · {company.contact_email}</div>
          <div className="flex items-center gap-2">
            <span className="pulse-dot" />
            <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#d4a54b]/60">WXZA FORGE™ v1.0</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

const NavBtn = ({ to, active, icon, label }) => (
  <Link
    to={to}
    className={`px-3 py-2 text-[13px] rounded-md flex items-center gap-2 transition-colors ${active ? "bg-[rgba(212,165,75,0.12)] text-[#e0bc62]" : "text-white/60 hover:text-white hover:bg-white/5"}`}
  >
    {icon} {label}
  </Link>
);

export default Layout;
